import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { Button, Tabs, Tab, Snackbar, Alert, Divider } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DescriptionIcon from '@mui/icons-material/Description';
import { Box, Typography, CircularProgress } from '@mui/material';

import ActionItems from './ActionItems';
import CompleteMeeting from './CompleteMeeting';
import { useFetchMeetingsDetails } from '../hooks/useFetchMeetingsDetails';
import { Comments, DrawerControl, DialogBox } from '../../../shared/components';
import MeetingDetails from './MeetingDetails';
import { CreateMeeting } from './Create';
import { meetingManager } from '../api/meetings';
import { useCapabilities } from '../../../hooks/useCapabilities';
import MeetingDetailsComponent from './MeetingDetails';
import { useFetchMeetings } from '../hooks/useFetchMeetings';

interface MeetingPanelProps {
  meeting_id: string;
  onMarkComplete: () => void;
}

const MeetingPanel: React.FC<MeetingPanelProps> = ({ meeting_id, onMarkComplete }) => {

  const [tabValue, setTabValue] = useState('meetingDetails');
  const [editMeetingOpen, setEditMeetingOpen] = React.useState(false);
  const [editMeetingSuccess, setEditMeetingSuccess] = React.useState(false);
  const [completeMeetingOpen, setCompleteMeetingOpen] = React.useState(false);
  const [momLoading, setMomLoading] = React.useState(false);
  const [momError, setMomError] = React.useState<string | null>(null);

  const { meetingDetails, isLoading, isError, error, refetch } = useFetchMeetingsDetails({
    meetingId: meeting_id as string,
  });
   const { meetingData, pagination, isLoading: isMeetingsLoading, isError: isMeetingsError, error: meetingsError, refetch: refetchMeetings } = useFetchMeetings({ projectId: null , page: 1, limit: 10, filterType: 'upcoming' });

  const { isAllowedToEditMeeting, isAllowedToCompleteMeeting } = useCapabilities();

  const [isCompleted, setIsCompleted] = React.useState(meetingDetails?.is_completed);

  const navigate = useNavigate();
  const location = useLocation();

  const handleEditMeetingOpen = () => setEditMeetingOpen(true);

  useEffect(() => {
    setIsCompleted(meetingDetails?.is_completed);
  }, [meetingDetails]);

  const handleTabChange = (_e: React.SyntheticEvent, newValue: string) => {
    setTabValue(newValue);
    const params = new URLSearchParams(location.search);
    params.set('tab', newValue);
    params.set('meeting_id', meeting_id as string);
    navigate(`?${params.toString()}`, { replace: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabFromUrl = params.get('tab');
    if (tabFromUrl) {
      setTabValue(tabFromUrl);
    }
  }, [location.search]);

  const handleCompleteMeetingOpen = () => setCompleteMeetingOpen(true);
  const handleCompleteMeetingClose = () => setCompleteMeetingOpen(false);

  const handleGenerateMoM = async () => {
    try {
      setMomLoading(true);
      setMomError(null);
      const blob = await meetingManager.generateMOM(meeting_id);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${meeting_id}.pdf`;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error: any) {
      setMomError(error.message || 'Failed to generate MoM');
      console.error('Error generating MoM:', error);
    } finally {
      setMomLoading(false);
    }
  };

  if (!meeting_id) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">Nothing is here!</Typography>
          <Typography variant="body2">Please select a meeting from the list</Typography>
        </Box>
      </Box>
    );
  };

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '100%',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        height: '100%',
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >

      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="basic tabs example">
          <Tab label="Meeting Details" value="meetingDetails" />
          <Tab label="Action Items" value="actionItems" />
          <Tab label="Comments" value="comments" />
        </Tabs>
        <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto', gap: 2, pr: 2 }}>
          {isAllowedToEditMeeting && <Button
            variant="outlined"
            startIcon={<EditIcon sx={{ height: 12, width: 12 }} />}
            size="small"
            onClick={handleEditMeetingOpen}
            disabled={isCompleted}
          >
            Edit
          </Button>}
          <Button
            startIcon={<DescriptionIcon sx={{ height: 15, width: 15 }} />}
            variant="outlined"
            size="small"
            sx={{
              boxShadow: 'none',
            }}
            onClick={handleGenerateMoM}
            disabled={momLoading || isCompleted}
          >
            {momLoading ? <CircularProgress size={16} /> : 'Generate MoM'}
          </Button>
          {isAllowedToCompleteMeeting && <Button
            startIcon={<CheckCircleIcon sx={{ height: 15, width: 15 }} />}
            variant="outlined"
            size="small"
            onClick={handleCompleteMeetingOpen}
            sx={{
              boxShadow: 'none',
            }}
            disabled={isCompleted}
          >
            Mark Complete
          </Button>}
        </Box>
      </Box>

      <Divider />

      <Box sx={{ flex: 1, overflow: 'hidden', p: 2 }}>
        {tabValue === 'meetingDetails' && <MeetingDetailsComponent meetingId={meeting_id as string} meetingDetails={meetingDetails} isLoading={isLoading} isError={isError} error={error} refetch={refetch} />}
        {tabValue === 'actionItems' && <ActionItems meeting_id={meeting_id} meetingDetails={meetingDetails} />}
        {tabValue === 'comments' && <Comments topic={['MEETING_COMMENT']} sub_event={['MEETING']} object_id={meeting_id as string} parent_id={undefined} />}
      </Box>

      <DialogBox
        title={'Create Meeting'}
        subTitle={''}
        children={
          <CreateMeeting
            onClose={() => { setEditMeetingOpen(false); }}
            refetch={refetch}
            refetchMeetings={refetchMeetings} 
            mode='edit'
            initialData={meetingDetails}
          />
        }
        showModal={editMeetingOpen}
        onModalClose={() => setEditMeetingOpen(false)}
        fullWidth
      />

      <DrawerControl
        isOpen={completeMeetingOpen}
        handleClose={handleCompleteMeetingClose}
        title="Complete Meeting"
        subTitle=""
        children={
          <CompleteMeeting
            taskId={meeting_id}
            onClose={() => { setCompleteMeetingOpen(false) }}
            refetch={() => {
              refetch();
              onMarkComplete();
            }}
          />
        }
      />

      <Snackbar
        open={editMeetingSuccess}
        autoHideDuration={2000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => {
          setEditMeetingSuccess(false);
        }}
      >
        <Alert
          onClose={() => {
            setEditMeetingSuccess(false);
          }}
          sx={{ width: '100%' }}
          severity="success"
        >
          Meeting updated successfully
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!momError}
        autoHideDuration={3000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => {
          setMomError(null);
        }}
      >
        <Alert
          onClose={() => {
            setMomError(null);
          }}
          sx={{ width: '100%' }}
          severity="error"
        >
          {momError}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default MeetingPanel;